import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { Observable, tap, BehaviorSubject, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, LoginResponse, LoginCredentials, SignupCredentials, PasswordResetConfirmData } from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private cookieService = inject(CookieService);
  
  private readonly baseUrl = environment.apiUrl;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
  }

  get accessToken(): string {
    return this.cookieService.get('access_token');
  }

  get refreshToken(): string {
    return this.cookieService.get('refresh_token');
  }

  get isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  public hydrateUser() {
    if (this.isAuthenticated) {
      this.getUser().pipe(
        catchError((err) => {
          console.error('Error hydrating user', err);
          return of(null);
        })
      ).subscribe();
    }
  }

  signup(data: SignupCredentials): Observable<User> {
    return this.http.post<User>(`${this.baseUrl}/api/auth/users/`, data);
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}/api/auth/jwt/create/`, credentials).pipe(
      tap((res: LoginResponse) => {
        if (res.access && res.refresh) {
          const cookieOptions: CookieOptions = { path: '/', sameSite: 'Lax', secure: environment.production };
          this.cookieService.set('access_token', res.access, { ...cookieOptions, expires: 1 });
          this.cookieService.set('refresh_token', res.refresh, { ...cookieOptions, expires: 7 });
          this.hydrateUser();
        }
      })
    );
  }

  refreshAccessToken(refresh: string): Observable<{ access: string }> {
    return this.http.post<{ access: string }>(`${this.baseUrl}/api/auth/jwt/refresh/`, { refresh }).pipe(
      tap((res) => {
        const cookieOptions: CookieOptions = { path: '/', sameSite: 'Lax', secure: environment.production, expires: 1 };
        this.cookieService.set('access_token', res.access, cookieOptions);
      })
    );
  }

  logout() {
    this.cookieService.delete('access_token', '/');
    this.cookieService.delete('refresh_token', '/');
    this.currentUserSubject.next(null);
  }

  getUser(): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/api/auth/users/me/`).pipe(
      tap((user: User) => this.currentUserSubject.next(user))
    );
  }

  resetPasswordRequest(email: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/auth/users/reset_password/`, { email });
  }

  resetPasswordConfirm(data: PasswordResetConfirmData): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/auth/users/reset_password_confirm/`, data);
  }

  activate(uid: string, token: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/auth/users/activation/`, { uid, token });
  }

  resendActivation(email: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/auth/users/resend_activation/`, { email });
  }
}
